import React, { useEffect, useState } from "react";
import { UserContext } from "../../context/UserContext";
import {
  UserCtx,
  DbUser,
  CourseStructureCtx,
  ClassExtended,
  School,
  AllCourses,
  InsightsCtx,
} from "../../interfaces";
import { Box, Grid, Link, Stack } from "@mui/material";
import { HomeHeader } from "../../components/v2/home/Header";
import { MiniProfileCard } from "../../components/v2/home/MiniProfileCard";
import { InspirationQuoteCard } from "../../components/v2/home/InspirationQuote";
import { useContext } from "react";
import { PageContainer } from "../../components/v2/PageContainer";
import { useHelpAndResources } from "../../hooks/useHelpAndResources";
import { useHistory, useLocation } from "react-router-dom";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { ClassListContainer } from "../../components/v3/home/ClassListContainer";
import { LessonPlansCard } from "../../components/v3/home/LessonPlansCard";
import { Role } from "../../enums";
import { SchoolSearch } from "../../components/v2/insights/Home/SchoolSearch";
import { TeacherSearch } from "../../components/v2/insights/Home/TeacherSearch";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { ReactComponent as IconFilter } from "../../assets/Icon-Filter.svg";
import { makeStyles } from "@mui/styles";
import { CustomChip } from "../../components/v2/utils/CustomChip";
import { FilterModal, FilterOptionProps } from "../../components/v3/utils/FilterModal";
import { InsightsContext } from "../../context/InsightsContext";
import { generateGtmEvent, HandleDataLayer, merge } from "../../Helpers";
import {
  classOverviewTeacher,
  classOverviewAdminSuper,
} from "../../components/utils/guidedTours";
import { Helmet } from "react-helmet-async";

const useStyle = makeStyles((theme) => ({
  filterButton: {
    backgroundColor: "transparent",
    borderRadius: "8px",
    color: theme.palette.text.secondary,
    width: "93px",
    height: "40px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#F2F2F2",
      width: "93px",
      height: "40px",
      color: theme.palette.text.primary,
      transform: "none",
    },
  },
  filterButtonActive: {
    backgroundColor: "#F2F2F2",
    width: "93px",
    height: "40px",
    color: theme.palette.text.primary,
    transform: "none",
    borderRadius: "8px",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#F2F2F2",
      width: "93px",
      height: "40px",
      color: theme.palette.text.primary,
      transform: "none",
    },
  },
  filterButtonIcon: {
    width: "16px",
    height: "16px",
  },
  filterButtonTextIcon: {
    width: "18px",
    height: "18px",
  },
  infoIcon: {
    cursor: "pointer",
    color: "#191919",
    transition: "color 200ms ease-in-out",
  },
  infoIconInactive: {
    color: "#00000042",
    transition: "color 200ms ease-in-out",
  },
}));

type PaginationResult = {
  filteredResults: ClassExtended[];
  totalResults: number;
  totalPages: number;
};

export const HomePage = () => {
  const history = useHistory();
  const location = useLocation();
  const classes = useStyle();
  const queryParams = new URLSearchParams(location.search);
  const queryOpenTour = (queryParams.get("guidedtour") as string) === "true";
  const isClassOverview = location.pathname === "/class-overview";
  const schoolId = queryParams.get("schoolId");
  const teacherId = queryParams.get("teacherId");
  const userCtx = useContext(UserContext) as UserCtx;
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const currentUser: DbUser = userCtx.dbUser;
  const initials = (currentUser.first_name[0] + currentUser.last_name[0]).toUpperCase();

  const [recentClassesActive, setRecentClassesActive] = useState<ClassExtended[] | null>(
    null
  );
  const [recentClassesArchived, setRecentClassesArchived] = useState<
    ClassExtended[] | null
  >(null);
  const [teacherClasses, setTeacherClasses] = useState<ClassExtended[] | null>(null);
  const [observerClasses, setObserverClasses] = useState<ClassExtended[] | null>(null);
  const [archivedClasses, setArchivedClasses] = useState<ClassExtended[] | null>(null);
  const [observedStudents, setObservedStudents] = useState<DbUser[] | null>(null);
  const [archivedObservedStudents, setArchivedObservedStudents] = useState<
    DbUser[] | null
  >(null);

  const [selectedSchool, setSelectedSchool] = React.useState<School | null>(null);
  const [selectedTeacher, setSelectedTeacher] = React.useState<DbUser | null>(null);

  const [page, setPage] = useState(1);
  // const setPage = (newNum: number) => {
  //   console.warn("setPage: ", newNum);
  //   setPageState(newNum);
  // };
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [classCourseFilters, setClassCourseFilters] = useState<FilterOptionProps[]>([]);
  const [availableFilters, setAvailableFilters] = useState<AllCourses[]>([]);

  const v3_design_seen_date = userCtx.dbUser.metadata?.v3_design_seen_date
    ? new Date(userCtx.dbUser.metadata?.v3_design_seen_date)
    : undefined;
  const [seenV3Design, setSeenV3Design] = useState<boolean>(
    v3_design_seen_date ? true : false
  );
  const twoWeeksAgo = new Date().getTime() - 2 * 7 * 24 * 60 * 60 * 1000; // current time minus 2 weeks in milliseconds
  const hideGuidedTourMessage = v3_design_seen_date
    ? v3_design_seen_date?.getTime() < twoWeeksAgo
    : false;

  const showRecentClasses =
    userCtx.dbUser.role === Role.teacher ? false : !teacherId && !schoolId;

  const skillRecommendationCourses =
    insightsCtx.courseList?.filter(
      (item) => !item.course_metadata?.exclude_from_skill_recommendations
    ) ?? [];

  // update seen v3 design metadata date
  useEffect(() => {
    if (
      userCtx.dbUser &&
      !seenV3Design &&
      !userCtx.dbUser.metadata?.v3_design_seen_date
    ) {
      try {
        setSeenV3Design(true);
        userCtx.updateUserMetadata({ v3_design_seen_date: new Date() });
      } catch (e) {
        console.error(e);
      }
    }
  }, [seenV3Design, userCtx]);

  useEffect(() => {
    if (!isClassOverview) return;
    const user = userCtx.dbUser;

    const recentlyViewedActive = insightsCtx.recentlyViewedActive;
    const recentlyViewedArchived = insightsCtx.recentlyViewedArchived;
    const classesActive = insightsCtx.classesActive;
    const observingClassesActive = insightsCtx.observingClassesActive;
    const classesArchived = insightsCtx.classesArchived;
    const observingClassesArchived = insightsCtx.observingClassesArchived;
    const studentsActive = insightsCtx.studentsActive;
    const studentsArchived = insightsCtx.studentsArchived;

    if (!user) return;

    setRecentClassesActive(recentlyViewedActive);
    setRecentClassesArchived(recentlyViewedArchived);
    setTeacherClasses(classesActive);
    setObserverClasses(observingClassesActive);
    setObservedStudents(studentsActive);

    setArchivedClasses(
      merge(
        classesArchived ?? [],
        observingClassesArchived ?? [],
        (a, b) => a.id === b.id
      )
    );
    // setArchivedClasses([...(classesArchived ?? []), ...(observingClassesArchived ?? [])]);
    setArchivedObservedStudents(studentsArchived);
  }, [
    isClassOverview,
    schoolId,
    teacherId,
    userCtx.dbUser,
    insightsCtx.recentlyViewedActive,
    insightsCtx.recentlyViewedArchived,
    insightsCtx.classesActive,
    insightsCtx.observingClassesActive,
    insightsCtx.classesArchived,
    insightsCtx.observingClassesArchived,
    insightsCtx.studentsActive,
    insightsCtx.studentsArchived,
  ]);

  const { element: HelpAndResourcesButton, handleOpenGuidedTour } = useHelpAndResources({
    toolTipProps: {
      steps:
        currentUser.role === Role.teacher
          ? classOverviewTeacher
          : classOverviewAdminSuper,
      parentId: "main",
    },
    showReminder: currentUser.user_progress ? true : false, // only display the reminder on home page if the user has previously started a problem
  });

  const renderData = {
    backgroundColor: "#191919",
    subheading: "GET STARTED…",
    heading: "Guided tour",
    content:
      "Welcome to your new dashboard! This tour will help you navigate through your classes, course content and student reports.",
    buttonText: "Launch tour",
    welcomeMsg: `Welcome back, ${userCtx.dbUser?.first_name}!`,
    onClick: () => {
      HandleDataLayer(
        "modal",
        "open",
        "click",
        "main content",
        "guided tour",
        "launch tour",
        "onclick",
        "home"
      );
      // set v3 design seen date to 3 weeks ago to immediately hide guided tour upon clicking
      const threeWeeksAgo = new Date(new Date().getTime() - 21 * 86400000);
      userCtx.updateUserMetadata({ v3_design_seen_date: threeWeeksAgo });
      handleOpenGuidedTour();
    },
  };

  useEffect(() => {
    if (queryOpenTour) {
      handleOpenGuidedTour();
      // clears the search query
      history.push("/learning-overview");
    }
  }, [queryOpenTour, handleOpenGuidedTour, history]);

  const getClassesContainerHeading = () => {
    if (currentUser.role === Role.teacher) {
      return "Your classes";
    } else if (currentUser.role === Role.admin) {
      return "Classes at your school";
    } else if (currentUser.role === Role.super) {
      return "All classes";
    } else {
      return "Classes";
    }
  };

  useEffect(() => {
    if (userCtx.dbUser.role === Role.admin) {
      setSelectedSchool(userCtx.dbUser.user_school[0]?.school!);
    } else if (userCtx.dbUser.role === Role.super) {
      setSelectedSchool(null);
      setSelectedTeacher(null);
    } else {
      setSelectedSchool(null);
    }
  }, [userCtx.dbUser.role, userCtx.dbUser.user_school]);

  useEffect(() => {
    if (userCtx.dbUser.role === Role.teacher) {
      history.push({
        pathname: "/class-overview",
        search: `?schoolId=${userCtx.dbUser.user_school[0]?.school_id}&teacherId=${userCtx.dbUser.id}`,
      });
    }
  }, [history, userCtx.dbUser]);

  const handleFilter = (filter: FilterOptionProps) => {
    setClassCourseFilters(classCourseFilters.filter((f) => f.id !== filter.id));
  };

  useEffect(() => {
    if (courseStructureCtx.allCourses) {
      setAvailableFilters(courseStructureCtx.allCourses);
    }
  }, [courseStructureCtx.allCourses]);

  const handlePageChange = (newPage: number, results: PaginationResult) => {
    const { totalPages } = results;
    const clampedPage = Math.max(1, Math.min(newPage, totalPages));
    setPage(clampedPage);
  };

  const applyFilterAndPagination = (
    inClasses: ClassExtended[],
    resultsPerPage: number,
    page: number, // start at 1
    courseIds?: number[]
  ): PaginationResult => {
    if (userCtx.dbUser.role === Role.teacher) {
      return {
        filteredResults: inClasses,
        totalResults: inClasses.length,
        totalPages: 1,
      };
    }

    const sortedAndFiltered = inClasses
      .sort((a, b) => {
        const dateA =
          a.settings?.viewed_at instanceof Date
            ? a.settings.viewed_at.getTime()
            : a.settings?.viewed_at
            ? new Date(a.settings.viewed_at).getTime()
            : 0;
        const dateB =
          b.settings?.viewed_at instanceof Date
            ? b.settings.viewed_at.getTime()
            : b.settings?.viewed_at
            ? new Date(b.settings.viewed_at).getTime()
            : 0;
        return dateB - dateA; // descending order, most recent first
      })
      .filter(
        (item) =>
          !courseIds || courseIds.length < 1 || courseIds.includes(item.course_id ?? 1)
      );

    const totalResults = sortedAndFiltered.length;
    const totalPages = Math.ceil(totalResults / resultsPerPage);
    const filteredResults = sortedAndFiltered.slice(
      (page - 1) * resultsPerPage,
      page * resultsPerPage
    );

    return {
      filteredResults,
      totalResults,
      totalPages,
    };
  };

  const NothingSelectedErrorMessage = (message: string) => {
    return (
      <>
        <Box
          sx={{
            background: "#E9F5FE 0% 0% no-repeat padding-box",
            width: "100%",
            height: "85px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Typography
            sx={{ font: "normal normal normal 16px/24px Roboto", color: "#191919" }}>
            <i className={`far fa-circle-info fa-lg ${classes.infoIcon}`}></i>
            <span style={{ marginLeft: "10px" }}>{message}</span>
          </Typography>
        </Box>
      </>
    );
  };

  const NoResultFoundErrorMessage = () => {
    let text = "";
    if (schoolId && teacherId) {
      text =
        "The selected teacher may not have any active classes. Create a class for this teacher in the Management page.";
    } else if (schoolId && !teacherId) {
      text =
        "The selected school may not have any active classes. Create classes for this school in the Management page.";
    }
    return (
      <Stack direction={"column"} gap={"8px"}>
        <Typography
          sx={{ font: "normal normal bold 24px/54px proxima-soft", color: "#191919" }}>
          Sorry, we couldn’t find a match.
        </Typography>
        <Typography
          sx={{
            font: "normal normal normal 16px/24px Roboto",
            color: "#5D5D5D",
            marginTop: "8px",
          }}>
          Suggestions:
        </Typography>
        <Typography
          sx={{ font: "normal normal normal 16px/24px Roboto", color: "#5D5D5D" }}>
          • Try clearing any active filters.
        </Typography>
        <Typography
          sx={{ font: "normal normal normal 16px/24px Roboto", color: "#5D5D5D" }}>
          • {text}
        </Typography>
      </Stack>
    );
  };
  const GetNoDataText = () => {
    if (userCtx.dbUser.role === Role.teacher) {
      return "You have not been assigned to any classes yet";
    } else if (userCtx.dbUser.role === Role.admin) {
      if (!teacherId) {
        return NothingSelectedErrorMessage(
          "Select a teacher to view classes. Use the Filter button to filter by course."
        );
      } else {
        return NoResultFoundErrorMessage();
      }
    } else if (userCtx.dbUser.role === Role.super) {
      if (!schoolId && !teacherId) {
        return NothingSelectedErrorMessage(
          "Select a school and teacher to view classes. Use the Filter button to filter by course."
        );
      } else {
        return NoResultFoundErrorMessage();
      }
    }
  };

  // show recent classes if no teacher or school selected and user is not teacher
  // otherwise show teacherClasses results
  const filteredClasses = applyFilterAndPagination(
    showRecentClasses ? recentClassesActive ?? [] : teacherClasses ?? [],
    6,
    page,
    classCourseFilters.map((item) => Number(item.id))
  );

  const filteredArchivedClasses = showRecentClasses
    ? recentClassesArchived ?? []
    : archivedClasses ?? [];

  const loadingClassData =
    schoolId !== null && insightsCtx.checkSchoolClassesLoading(Number(schoolId));

  return (
    <>
      <Helmet>
        <title>Home | Digit</title>
      </Helmet>
      <PageContainer>
        <Grid container spacing={10}>
          <Grid item xs={12} sm={8} component="section">
            <Box sx={{ maxWidth: 846, marginRight: "auto" }}>
              <HomeHeader
                userName={currentUser.full_name}
                renderData={renderData}
                hideCard={hideGuidedTourMessage}
              />
              <ClassListContainer
                classesExtended={filteredClasses.filteredResults}
                heading={getClassesContainerHeading()}
                loadingClassData={loadingClassData}
                classProgressDataMap={insightsCtx.classProgressDataMap}
                subHeading={
                  showRecentClasses ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ marginBottom: "24px" }}>
                      <i
                        className={`fa-regular fa-clock fa-sm`}
                        style={{ marginRight: "8px" }}></i>{" "}
                      Recently viewed
                    </Stack>
                  ) : (
                    <></>
                  )
                }
                noDataText={GetNoDataText()}
                mainId="classes-main-container"
                containerId="classes-class-container"
                paginationSelectorComponent={
                  userCtx.dbUser.role !== Role.teacher ? (
                    <>
                      <Stack
                        direction="row"
                        gap="16px"
                        alignItems="center"
                        sx={{
                          marginBottom: "24px",
                        }}>
                        {userCtx.dbUser.role === Role.super && (
                          <SchoolSearch
                            onSelect={(school) => {
                              if (school?.id !== selectedSchool?.id)
                                setSelectedTeacher(null);
                              setSelectedSchool(school);
                              setPage(1);
                              if (!school) {
                                setSelectedTeacher(null);
                                history.push({
                                  pathname: "/class-overview",
                                  search: new URLSearchParams({}).toString(),
                                });
                              }
                            }}
                            selected={selectedSchool}
                          />
                        )}
                        {selectedSchool ? (
                          <TeacherSearch
                            selected={selectedTeacher}
                            onSelect={(teacher) => {
                              setSelectedTeacher(teacher);
                              setPage(1);
                              if (!teacher) {
                                history.push({
                                  pathname: "/class-overview",
                                  search: new URLSearchParams({
                                    // only re-populate the schoolId parameter if super admin
                                    // otherwise prevents admins from seeing recently viewed when deselecting teacher
                                    ...(userCtx.dbUser.role === Role.super &&
                                      selectedSchool && {
                                        schoolId: `${selectedSchool?.id}`,
                                      }),
                                  }).toString(),
                                });
                              }
                            }}
                            schoolId={selectedSchool?.id!}
                          />
                        ) : null}
                        <Button
                          onClick={() => {
                            generateGtmEvent({
                              event: "modal",
                              action: "open",
                              name: "onclick",
                              type: "click",
                              region: "main content",
                              section: "all classes",
                              text: "filter",
                            });
                            setFilterModalOpen(true);
                          }}
                          size="medium"
                          className={
                            classCourseFilters.length > 0
                              ? classes.filterButtonActive
                              : classes.filterButton
                          }
                          variant="contained"
                          endIcon={
                            classCourseFilters.length > 0 ? (
                              <Box
                                sx={{ borderRadius: "50%", backgroundColor: "#000000" }}
                                className={classes.filterButtonTextIcon}>
                                <Typography
                                  sx={{
                                    color: "#ffffff",
                                    fontSize: "0.6em",
                                    fontWeight: "600",
                                  }}>
                                  {classCourseFilters.length}
                                </Typography>
                              </Box>
                            ) : (
                              <IconFilter className={classes.filterButtonIcon} />
                            )
                          }>
                          Filter
                        </Button>
                        <Box sx={{ marginLeft: "auto" }}>
                          {/* add right and left fontawesome chevron icon for pagination */}
                          <i
                            className={`fas fa-chevron-left fa-xl ${
                              page === 1 ? classes.infoIconInactive : classes.infoIcon
                            }`}
                            onClick={() =>
                              handlePageChange(page - 1, filteredClasses)
                            }></i>
                          <i
                            className={`fas fa-chevron-right fa-xl ${
                              page === filteredClasses.totalPages
                                ? classes.infoIconInactive
                                : classes.infoIcon
                            }`}
                            onClick={() =>
                              handlePageChange(page + 1, filteredClasses)
                            }></i>
                        </Box>
                      </Stack>
                      {classCourseFilters.length > 0 ? (
                        <Box
                          sx={{
                            marginTop: "24px",
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            marginBottom: "24px",
                            alignItems: "center",
                          }}>
                          {classCourseFilters.map((filter) => {
                            const filterObj = availableFilters.find(
                              (f) => f.id.toString() === filter.id
                            );
                            return (
                              <CustomChip
                                key={`filter-${filter.id}`}
                                label={filterObj?.name}
                                onClose={() => {
                                  generateGtmEvent({
                                    event: "select",
                                    action: "unclick",
                                    name: "onclick",
                                    type: "checkbox",
                                    region: "main content",
                                    section: "all classes",
                                    text: `${filterObj?.name}`.toLowerCase(),
                                  });
                                  handleFilter(filter);
                                  setPage(1);
                                }}
                              />
                            );
                          })}
                          <Link
                            onClick={() => {
                              generateGtmEvent({
                                event: "link",
                                action: "click",
                                name: "onclick",
                                type: "internal link",
                                region: "main content",
                                section: "all classes",
                                text: "clear all",
                              });
                              setClassCourseFilters([]);
                              setPage(1);
                            }}
                            variant="body1">
                            Clear all
                          </Link>
                        </Box>
                      ) : null}
                      <FilterModal
                        open={filterModalOpen}
                        availableFilters={availableFilters.map((filter) => {
                          return { id: filter.id.toString(), name: filter.name };
                        })}
                        appliedFilters={classCourseFilters}
                        onFilter={(filter) => {
                          setClassCourseFilters(filter);
                          setPage(1);
                        }}
                        title={"Filter"}
                        heading={"Courses"}
                        handleClose={() => {
                          setFilterModalOpen(false);
                        }}
                      />
                    </>
                  ) : (
                    <></>
                  )
                }
              />
              {currentUser.role === Role.teacher && (
                <ClassListContainer
                  classesExtended={observerClasses ?? []}
                  users={observedStudents ?? []}
                  heading="Classes and students you’re observing"
                  classProgressDataMap={insightsCtx.classProgressDataMap}
                  studentProgressDataMap={insightsCtx.studentProgressDataMap}
                  noDataText={
                    <>
                      <div style={{ marginBottom: "16px" }}>
                        You’re not observing any classes or students at the moment
                      </div>
                      <div>
                        Classes and students that are shared to you by other teachers will
                        appear here
                      </div>
                    </>
                  }
                />
              )}
              <ClassListContainer
                classesExtended={filteredArchivedClasses?.slice(0, 3) ?? []}
                users={
                  !filteredArchivedClasses || filteredArchivedClasses?.length < 3
                    ? archivedObservedStudents ?? []
                    : []
                }
                heading="Archived classes"
                subHeading={
                  showRecentClasses ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ marginBottom: "24px" }}>
                      <i
                        className={`fa-regular fa-clock fa-sm`}
                        style={{ marginRight: "8px" }}></i>{" "}
                      Recently viewed
                    </Stack>
                  ) : (
                    <></>
                  )
                }
                showSeeAll={archivedClasses ? archivedClasses?.length > 0 : false}
                showSeeAllPath="/archived-class-management"
                variant="grid"
                noDataText={
                  <>
                    <div style={{ marginBottom: "16px" }}>
                      You have no archived classes
                    </div>
                    <div>
                      Classes that have ended will be listed here for you to access
                    </div>
                  </>
                }
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} component="section">
            <Box sx={{ maxWidth: 378, marginLeft: "auto" }}>
              <MiniProfileCard
                currentUser={currentUser}
                initials={initials}
                fullName={currentUser.full_name}
                school={
                  currentUser.user_school.length > 0
                    ? currentUser.user_school[0]?.school?.name
                    : undefined
                }
              />
              {skillRecommendationCourses.length > 0 &&
                currentUser.role === Role.teacher && (
                  <>
                    <LessonPlansCard
                      containerId="lesson-plans-card"
                      teacherCourses={skillRecommendationCourses}
                      classesExtended={
                        teacherClasses && teacherClasses.length > 0
                          ? teacherClasses
                          : undefined
                      }
                    />
                    <InspirationQuoteCard />
                  </>
                )}
            </Box>
          </Grid>
        </Grid>
        {HelpAndResourcesButton}
      </PageContainer>
    </>
  );
};
